<script setup>
// The public root (/). A marketing page for signed-out visitors — authed users are
// redirected to their dashboard by the router guard. It extends the shipped civic /
// neo-brutalist system (cobalt, 2px ink borders, square corners, hard shadows,
// Poppins + JetBrains Mono, the 28px civic grid) and reuses the app's real
// money-flow components so the landing and the product share one visual grammar.
//
// Built to feel like scrolling through PAGES, not sections: every panel is a full
// screen tall, its content centred, and CSS scroll-snap (scoped to this route via
// the `snap-pages` class) settles each one into place. Each page below is its own
// self-contained component — it owns its markup, its `data-page-label` (for the
// pager) and its own GSAP reveal, the same pattern LandingStakes/FeatureDeck/HeroFlow
// already use. This file only owns the page shell and the right-edge pager, which
// tracks every page generically via that shared `data-page-label` attribute.
//
// The pages are ONE argument in sequence, and the order is the argument: the visitor
// has to recognise their own month (hero, probleem) and what it costs them (waarom)
// before Pandig describes itself (hoe het werkt, geldstroom, wat je ziet), and only
// then choose a path (voor wie), see the price, get their objections answered, and be
// asked. No page restates another page's point — the "Pandig raakt het geld niet aan"
// promise, for instance, is made on exactly one page, structurally, by the diagram.
//
// "A full screen" means `100dvh - var(--nav-h)`, never the whole viewport — the
// header is sticky and overlays every panel below the first (see .page-panel /
// .page-camera in application.css). Two rules keep the pages honest on a phone:
// each panel's content has to FIT that budget at phone widths, which is what all
// the mobile type/spacing steps below the `sm:` breakpoint are for; and the tall
// scroll-scrubbed sections carry a shorter scroll length on mobile than on desktop,
// since a viewport is a far bigger share of a touch flick than of a wheel tick.
// Landing.spec.js holds both rules to account.
//
// Because the pages are pages, the keys that mean "one screenful" have to mean "one
// page" — see the keyboard paging below.
import { ref, onMounted, onUnmounted } from "vue";
import LandingNav from "@/components/landing/LandingNav.vue";
import LandingHero from "@/components/landing/LandingHero.vue";
import LandingProblem from "@/components/landing/LandingProblem.vue";
import LandingStakes from "@/components/landing/LandingStakes.vue";
import LandingHow from "@/components/landing/LandingHow.vue";
import LandingFlows from "@/components/landing/LandingFlows.vue";
import FeatureDeck from "@/components/landing/FeatureDeck.vue";
import LandingAudiences from "@/components/landing/LandingAudiences.vue";
import LandingPricing from "@/components/landing/LandingPricing.vue";
import LandingFaq from "@/components/landing/LandingFaq.vue";
import LandingCta from "@/components/landing/LandingCta.vue";
import LandingFooter from "@/components/landing/LandingFooter.vue";
import { scrollElementIntoView, scrollTargetTop, resolveHash } from "@/lib/scroll";

const root = ref(null);

// The right-edge pager.
const pages = ref([]);
const pageLabels = ref([]);
const activePage = ref(0);
const hoveredPage = ref(null);
let observer = null;

// The tall scrubbed sections currently on screen, for the snap relaxation below.
const scrubsOnScreen = new Set();
let looseObserver = null;

// The jump currently in flight, so a second keypress counts from where the first one is
// going. See jumpTo.
let pendingTop = null;
let pendingTimer = 0;
const SETTLE_MS = 700;

function gotoPage(i, event) {
  const el = pages.value[i];
  if (!el) return;

  jumpTo(el);
  // A dot clicked with a mouse keeps focus afterwards, and a focused button owns the
  // space bar (there space means "press me"), so the next space press did nothing at
  // all — the page looked stuck. Keyboard activation (detail 0) keeps its focus, as it
  // must: tabbing to a dot and pressing it may not throw the focus away.
  if (event?.detail) event.currentTarget.blur();
}

// Move to a page the way every other jump on this route does, and remember where it is
// headed. A smooth scroll takes a few hundred ms, during which window.scrollY sits
// between two pages — so a reader pressing space twice in quick succession would have
// the second press ask for the page the first is already travelling to, and lose it.
// Counting from the destination while the jump is in flight makes two presses mean two
// pages. Afterwards the live position is the only truth again: by then the reader may
// have taken over with the wheel and be somewhere else entirely.
function jumpTo(el) {
  pendingTop = scrollTargetTop(el);
  clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => (pendingTop = null), SETTLE_MS);

  scrollElementIntoView(el);
}

// ---- Keyboard paging --------------------------------------------------------------
//
// A reader sees ten pages, so the keys that mean "one screenful" have to mean "one
// page". Left to the browser they didn't. A page here is 1.25–1.6 viewports tall (the
// tall sections scrub their reveal across the surplus), so the space bar — which moves
// about one viewport — came to rest inside the current page's own scroll range, where
// scroll-snap answered by pulling the reader part of the way back: press space, watch
// the page shuffle and stay roughly where it was. The arrow keys were worse still, a
// 40px nudge being well inside any snap threshold, so they moved nothing at all.
//
// All of them now go where the pager's dots go — a page's own resting position, with
// its reveal drawn. Nothing is skipped by that: the scroll travels through the pages in
// between, and a scrub tied to scroll position plays out as it passes.
const PAGE_FORWARD = ["PageDown", "ArrowDown"];
const PAGE_BACK = ["PageUp", "ArrowUp"];

// Space belongs to the focused control before it belongs to the page: it opens an FAQ
// answer, presses a pager dot, opens the mobile menu.
const TAKES_SPACE = "button, summary, [role='button']";
const EDITS_TEXT = "input, textarea, select, [contenteditable]:not([contenteditable='false'])";

// A page's resting position is rarely met to the pixel — snapping trims it, a mobile
// toolbar shifts it — so "the next page" is the next one a comfortable distance away,
// not merely one pixel away.
const SLACK = 8;

function onKeydown(event) {
  // Someone else's keypress: one a widget has already handled, a browser shortcut
  // (⌘↓ is "end of document"), or auto-repeat, which would machine-gun through the
  // pages while a key is held. One press, one page.
  if (event.defaultPrevented || event.repeat) return;
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  // Something is deliberately holding the page still — the mobile menu locks scrolling
  // on <html> while it's open — and nothing should move underneath it.
  if (document.documentElement.style.overflow === "hidden") return;

  const target = event.target;
  if (target?.closest?.(EDITS_TEXT)) return;

  const space = event.key === " " || event.key === "Spacebar";
  if (space && target?.closest?.(TAKES_SPACE)) return;

  const forward = PAGE_FORWARD.includes(event.key) || (space && !event.shiftKey);
  const back = PAGE_BACK.includes(event.key) || (space && event.shiftKey);
  if (!forward && !back) return;

  const direction = forward ? 1 : -1;
  // A box the focus is sitting in gets the key first if it still has room to scroll: the
  // FAQ's camera holds more than a phone screen once an answer is open, and with focus
  // on that answer's summary the arrow keys are the only way a keyboard reader reaches
  // the rest of it. Paging away from it would put it out of reach entirely.
  if (insideScrollable(target, direction)) return;

  const el = adjacentPage(direction);
  // Past the last page lies the footer, and above the first lies nothing: leave both to
  // the browser, which reaches them the ordinary way.
  if (!el) return;

  event.preventDefault();
  jumpTo(el);
}

// The page below (or above) where the reader is now, chosen by position rather than by
// the pager's active index. Inside a tall section the centre line still reads as "this
// page", and a keypress halfway through one has to move on to the next rather than jump
// back to where that page rests.
function adjacentPage(direction) {
  const from = pendingTop ?? window.scrollY;
  const stops = pages.value.map((el) => ({ el, top: scrollTargetTop(el) }));
  if (direction < 0) stops.reverse();

  return stops.find(({ top }) => (direction > 0 ? top > from + SLACK : top < from - SLACK))?.el ?? null;
}

// Whether an ancestor of `el` is a scroll box with room left in `direction`. Stops short
// of <body>/<html>, since the document itself is exactly what we're paging.
function insideScrollable(el, direction) {
  for (let node = el; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const room =
      direction > 0 ? node.scrollHeight - node.clientHeight - Math.ceil(node.scrollTop) : Math.floor(node.scrollTop);
    if (room > 1 && /auto|scroll/.test(getComputedStyle(node).overflowY)) return true;
  }
  return false;
}

onMounted(() => {
  document.documentElement.classList.add("snap-pages");

  // Track which page owns the viewport centre-line, for the pager. Keyed off
  // data-page-label (not .snap-page) — LandingStakes and FeatureDeck are real pages
  // for pager purposes even though they deliberately opt out of CSS scroll-snap.
  pages.value = [...root.value.querySelectorAll("[data-page-label]")];
  pageLabels.value = pages.value.map((page) => page.dataset.pageLabel ?? "");
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = pages.value.indexOf(entry.target);
        if (idx !== -1) activePage.value = idx;
      });
    },
    // A band around the centre line, not an exact zero-height line — a single line
    // is one frame away from missing a transition entirely (e.g. two entries firing
    // the same tick at a page boundary, or a fast fling skipping past it).
    { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
  );
  pages.value.forEach((page) => observer.observe(page));

  // Mandatory snapping is only honest where a real page can be snapped to. The band
  // between a tall scrubbed section's last covering position and the next page's
  // snap point holds no valid snap position at all, and `y mandatory` answers a
  // gesture ending in its first half by yanking the reader back to the section's
  // bottom edge — a slow wheel could never crawl from Prijs into Vragen. So while
  // any tall section is on screen, the container relaxes to proximity
  // (html.snap-pages-loose, application.css): a gesture ending near a page still
  // pulls square onto it, but nothing ever drags the reader backwards.
  const scrubs = pages.value.filter((page) => !page.classList.contains("snap-page"));
  looseObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) scrubsOnScreen.add(entry.target);
      else scrubsOnScreen.delete(entry.target);
    });
    document.documentElement.classList.toggle("snap-pages-loose", scrubsOnScreen.size > 0);
  });
  scrubs.forEach((scrub) => looseObserver.observe(scrub));

  window.addEventListener("keydown", onKeydown);

  // A shared /#prijzen link has to land on the price. The browser's own attempt
  // happened before any of this existed; this is the one that counts.
  resolveHash();
});

onUnmounted(() => {
  observer?.disconnect();
  looseObserver?.disconnect();
  window.removeEventListener("keydown", onKeydown);
  clearTimeout(pendingTimer);
  document.documentElement.classList.remove("snap-pages", "snap-pages-loose");
});
</script>

<template>
  <div ref="root" class="flex min-h-dvh flex-col">
    <LandingNav />

    <!-- Page pager: one square per full-screen panel. The squares alone said "there
         are ten pages" without saying what was on them, so the active one — and any
         one you point at — names itself. Pointer-events stay off the label so it can
         never intercept a click meant for the dot underneath it. -->
    <nav class="fixed right-4 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-3 lg:flex" aria-label="Pagina's">
      <button
        v-for="(page, i) in pages"
        :key="i"
        type="button"
        class="group relative size-2.5 border-2 border-foreground transition-all duration-200 hover:scale-125"
        :class="activePage === i ? 'scale-125 bg-foreground' : 'bg-transparent'"
        :aria-label="`Ga naar ${pageLabels[i]}`"
        :aria-current="activePage === i"
        @click="gotoPage(i, $event)"
        @pointerenter="hoveredPage = i"
        @pointerleave="hoveredPage = null"
        @focus="hoveredPage = i"
        @blur="hoveredPage = null"
      >
        <span
          class="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap border-2 border-foreground bg-card px-2 py-0.5 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-foreground shadow-[var(--shadow-hard-sm)] transition-opacity duration-200"
          :class="hoveredPage === i || activePage === i ? 'opacity-100' : 'opacity-0'"
        >
          {{ pageLabels[i] }}
        </span>
      </button>
    </nav>

    <main class="flex-1">
      <!-- 1. The problem, named, plus the money-flow signature. -->
      <LandingHero />
      <!-- 2. The three moments a board or manager recognises. -->
      <LandingProblem />
      <!-- 3. What an arrear actually costs — the one cinematic beat. -->
      <LandingStakes />
      <!-- 4. The mechanism: how a month runs once it's set up. -->
      <LandingHow />
      <!-- 5. Where the money goes, proved by the diagram rather than claimed. -->
      <LandingFlows />
      <!-- 6. What you see when a month doesn't go to plan, as a deck you scroll. -->
      <FeatureDeck />
      <!-- 7. The fork: a volunteer board, or a professional portfolio. -->
      <LandingAudiences />
      <!-- 8. Price, what sits on top of it, and how to just ask. -->
      <LandingPricing />
      <!-- 9. The seven objections that block a purchase. -->
      <LandingFaq />
      <!-- 10. The ask. -->
      <LandingCta />
    </main>

    <LandingFooter />
  </div>
</template>
